import * as Sentry from '@sentry/react';
import { type Query, type UseQueryOptions, useQuery } from '@tanstack/react-query';
import { SENTRY_FEATURE_TAGS, SENTRY_STAGE_TAGS } from '@vizit/constants';
import { getImageV1WorkspacesWorkspaceIdAssetsImagesImageIdGet } from '../../generated';
import type { GetImageV1WorkspacesWorkspaceIdAssetsImagesImageIdGetData } from '../../generated';
import type { GetImageV1WorkspacesWorkspaceIdAssetsImagesImageIdGetError } from '../../generated';
import type { GetImageV1WorkspacesWorkspaceIdAssetsImagesImageIdGetResponse } from '../../generated';
import { PENDING_STALE_TIMEOUT_MS, pollWhilePending } from '../../utils/poll-while-pending';

export type GetImageQueryVariables = GetImageV1WorkspacesWorkspaceIdAssetsImagesImageIdGetData['path'] & {
  organization_id: string;
};

export type GetImageQuery = GetImageV1WorkspacesWorkspaceIdAssetsImagesImageIdGetResponse;

/**
 * Pending when the relevant score is missing, when a hero image's mobile
 * readiness is mid-pipeline, or when the image URL hasn't landed yet (the API
 * head-checks S3 and returns null until the resize variant exists — on the
 * ISS path this happens AFTER scoring). Mobile fields resolve all-together
 * via an async SQS→lambda pipeline, so checking one is enough; strict
 * `=== null` skips polling when the backend doesn't return the field at all.
 *
 * URL pending is checked as `== null` only, NOT via the shared
 * `isImageUrlPending`'s '/images' placeholder heuristic — that heuristic
 * exists for LIST queries, where an optimistic insert can leave an S3 upload
 * URL sitting in the cache. A single-image GET response never contains that
 * placeholder, so applying it here would misclassify any resolved image
 * whose URL happens to omit '/images' (e.g. a CDN or test fixture URL) as
 * still pending.
 */
export const getImageDetailPendingTimestamps = (data: GetImageQuery | undefined): Array<string | null> => {
  if (!data) return [];
  const isHero = data.image_type === 'hero';
  const relevantScore = isHero ? data.hero_score : data.vizit_score;
  // '' matches usePdpImages' pending semantics (an empty-string vizit_score is
  // still processing); strict `=== null` (not undefined) stays deliberate so an
  // absent field (e.g. pre-deploy backend) never triggers polling.
  const scorePending = relevantScore === null || relevantScore === '' || (isHero && data.mobile_brand_clarity === null);
  // Anchor on updated_at ?? created_at: rescore/refresh flows null the score
  // on an old image but touch updated_at, so a created_at anchor never polls.
  return scorePending || data.url == null ? [data.updated_at ?? data.created_at ?? null] : [];
};

/**
 * Builds a `refetchInterval` callback for a single-image GET query: polls at
 * a fixed interval while the image's score or URL is still pending, and
 * stops (returns `false`) once resolved or past the stale window. Also wires
 * up Sentry breadcrumbs on each poll and a warning capture on timeout.
 *
 * `sentry` tags the timeout capture and defaults to the `Asset` feature's
 * `assetPolling` stage (the single-image details view). Callers polling from
 * a different surface should pass their own tags — e.g. `use-pdp-images`
 * passes the `Product` feature's `productCarouselImagesPolling` stage since
 * it polls PDP carousel images rather than the asset details page.
 */
export const buildGetImageRefetchInterval = <TError = unknown>(
  { organization_id, workspace_id, image_id }: GetImageQueryVariables,
  sentry: { feature: string; stage: string } = {
    feature: SENTRY_FEATURE_TAGS.Asset,
    stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Asset].assetPolling,
  },
) =>
  pollWhilePending((query: Query<GetImageQuery, TError>) => getImageDetailPendingTimestamps(query.state.data), {
    onPoll: () => {
      Sentry.addBreadcrumb({
        message: 'Polling for image url/score',
        level: 'info',
        data: { asset_id: image_id, organization_id, workspace_id },
      });
    },
    onTimeout: () => {
      Sentry.withScope((scope) => {
        scope.setFingerprint(['GET', '/v1/workspaces/{workspace_id}/assets/images/{image_id}', 'polling_timeout']);

        Sentry.captureException(new Error('Image still pending after stale window for get image query.'), {
          level: 'warning',
          tags: { feature: sentry.feature, stage: sentry.stage },
          extra: {
            organization_id,
            workspace_id,
            asset_id: image_id,
            stale_window_ms: PENDING_STALE_TIMEOUT_MS,
          },
        });
      });
    },
  });

export const useGetImageQuery = <
  TData = GetImageQuery,
  TError = GetImageV1WorkspacesWorkspaceIdAssetsImagesImageIdGetError,
>(
  { organization_id, workspace_id, image_id }: GetImageQueryVariables,
  options?: Omit<UseQueryOptions<GetImageQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<GetImageQuery, TError, TData>({
    queryKey: ['GetImage', { organization_id, workspace_id, image_id }],
    queryFn: async () => {
      const { data } = await getImageV1WorkspacesWorkspaceIdAssetsImagesImageIdGet({
        path: { workspace_id, image_id },
        throwOnError: true,
      });
      return data;
    },
    refetchInterval: buildGetImageRefetchInterval<TError>({ organization_id, workspace_id, image_id }),
    refetchIntervalInBackground: true,
    ...options,
  });
};

useGetImageQuery.getKey = ({ organization_id, workspace_id, image_id }: GetImageQueryVariables) => [
  'GetImage',
  { organization_id, workspace_id, image_id },
];

useGetImageQuery.fetcher = ({ workspace_id, image_id }: GetImageQueryVariables, _headers?: RequestInit['headers']) => {
  return async () => {
    const { data } = await getImageV1WorkspacesWorkspaceIdAssetsImagesImageIdGet({
      path: { workspace_id, image_id },
      throwOnError: true,
    });
    return data;
  };
};

useGetImageQuery.schema = {
  method: 'get',
  path: '/v1/workspaces/{workspace_id}/assets/images/{image_id}',
};
